/**
 * Two-Tier Category Navigation Component
 *
 * Linear-inspired minimal navigation with primary and secondary tiers.
 * Primary: Overview, Core, Advanced, Patterns
 * Secondary: Conditional based on primary selection
 *
 * @module concept-graph/components/TwoTierCategoryNav
 */

import React, { useState } from 'react';
import type { ConceptCategory } from '../types';

export type PrimaryCategory = 'overview' | 'core' | 'advanced' | 'patterns';

export interface TwoTierCategoryNavProps {
  /** Selected primary category */
  selectedPrimary: PrimaryCategory;
  /** Selected secondary category (or null for 'all') */
  selectedSecondary: ConceptCategory | null;
  /** Primary category selection handler */
  onPrimaryChange: (primary: PrimaryCategory, secondary: ConceptCategory | null) => void;
  /** Secondary category selection handler */
  onSecondaryChange: (secondary: ConceptCategory | null) => void;
  /** Concept counts per category */
  conceptCounts?: Record<ConceptCategory | 'all', number>;
  /** Additional CSS classes */
  className?: string;
}

// Category mapping structure
const CATEGORY_STRUCTURE = {
  overview: {
    label: 'Overview',
    description: 'All concepts',
    secondaries: [] as ConceptCategory[]
  },
  core: {
    label: 'Core',
    description: 'Fundamental programming concepts',
    secondaries: ['memory_management', 'types', 'control_flow', 'error_handling'] as ConceptCategory[]
  },
  advanced: {
    label: 'Advanced',
    description: 'Advanced programming techniques',
    secondaries: ['concurrency', 'io', 'modules', 'metaprogramming'] as ConceptCategory[]
  },
  patterns: {
    label: 'Patterns',
    description: 'Design patterns and paradigms',
    secondaries: ['design_patterns', 'paradigms', 'data_structures', 'functions'] as ConceptCategory[]
  }
};

// Secondary category labels
const SECONDARY_LABELS: Record<ConceptCategory, string> = {
  memory_management: 'Memory',
  types: 'Types',
  control_flow: 'Control',
  error_handling: 'Errors',
  concurrency: 'Concurrency',
  io: 'I/O',
  modules: 'Modules',
  metaprogramming: 'Meta',
  design_patterns: 'Design',
  paradigms: 'Paradigms',
  data_structures: 'Data',
  functions: 'Functions'
};

/**
 * Calculate total count for a primary category
 */
function getPrimaryCount(
  primary: PrimaryCategory,
  conceptCounts?: Record<ConceptCategory | 'all', number>
): number {
  if (primary === 'overview') {
    return conceptCounts?.all || 0;
  }

  const secondaries = CATEGORY_STRUCTURE[primary].secondaries;
  return secondaries.reduce((sum, cat) => sum + (conceptCounts?.[cat] || 0), 0);
}

/**
 * Two-tier category navigation with Linear-style minimal design
 */
export function TwoTierCategoryNav({
  selectedPrimary,
  selectedSecondary,
  onPrimaryChange,
  onSecondaryChange,
  conceptCounts,
  className = ''
}: TwoTierCategoryNavProps) {
  const showSecondary = selectedPrimary !== 'overview' && CATEGORY_STRUCTURE[selectedPrimary].secondaries.length > 0;

  return (
    <div className={`bg-white border-b border-slate-200/80 ${className}`}>
      {/* Primary Tier - Always Visible */}
      <div className="px-6 pt-3 pb-2">
        <div className="flex items-center gap-2">
          {(Object.keys(CATEGORY_STRUCTURE) as PrimaryCategory[]).map(primary => {
            const config = CATEGORY_STRUCTURE[primary];
            const count = getPrimaryCount(primary, conceptCounts);
            const isActive = selectedPrimary === primary;

            return (
              <button
                key={primary}
                onClick={() => onPrimaryChange(primary, null)}
                className={`
                  group relative px-4 py-1.5 rounded-md text-sm font-medium transition-all duration-200
                  ${isActive
                    ? 'text-slate-900 bg-slate-100'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                  }
                `}
                title={config.description}
                data-testid={`primary-category-${primary}`}
              >
                <div className="flex items-center gap-2">
                  <span>{config.label}</span>
                  {count > 0 && (
                    <span
                      className={`
                        text-xs px-1.5 py-0.5 rounded-full font-semibold
                        ${isActive
                          ? 'bg-slate-200 text-slate-700'
                          : 'bg-slate-100 text-slate-500 group-hover:bg-slate-200 group-hover:text-slate-700'
                        }
                      `}
                    >
                      {count}
                    </span>
                  )}
                </div>

                {/* Active indicator - Linear style underline */}
                {isActive && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-slate-900 rounded-full" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Secondary Tier - Conditional */}
      {showSecondary && (
        <div className="px-6 pb-3">
          <div className="flex items-center gap-1.5">
            {/* "All" button for selected primary */}
            <button
              onClick={() => onSecondaryChange(null)}
              className={`
                px-3 py-1 rounded-md text-xs font-medium transition-all duration-200
                ${selectedSecondary === null
                  ? 'text-slate-900 bg-slate-100 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                }
              `}
              data-testid="secondary-category-all"
            >
              All
            </button>

            {/* Divider */}
            <div className="w-px h-4 bg-slate-200 mx-1" />

            {/* Secondary category pills */}
            {CATEGORY_STRUCTURE[selectedPrimary].secondaries.map(secondary => {
              const count = conceptCounts?.[secondary] || 0;
              const isActive = selectedSecondary === secondary;

              return (
                <button
                  key={secondary}
                  onClick={() => onSecondaryChange(secondary)}
                  className={`
                    px-3 py-1 rounded-md text-xs font-medium transition-all duration-200
                    ${isActive
                      ? 'text-slate-900 bg-slate-100 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                    }
                  `}
                  data-testid={`secondary-category-${secondary}`}
                >
                  <div className="flex items-center gap-1.5">
                    <span>{SECONDARY_LABELS[secondary]}</span>
                    {count > 0 && (
                      <span className={`
                        text-[10px] px-1 py-0.5 rounded-full font-semibold
                        ${isActive ? 'bg-slate-200 text-slate-600' : 'bg-slate-100 text-slate-400'}
                      `}>
                        {count}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default TwoTierCategoryNav;
